/**
 * Gallery (V2) — ONE component per kind, composed from the Production Foundation. Originally a
 * literal clone of the reference "gallary" section (realDemo landing-gallary.tsx), which sizes
 * every tile in `vw` units and switches to a two-row spanning masonry (large square + two
 * stacked small squares, repeating) once there are more than five images. `vw` units work on
 * the reference's own full-bleed page, but inside the Manual Builder/Live Preview (a constrained
 * iframe/column, not the full browser viewport) that math no longer matches the visible width,
 * so tiles rendered far too tall and narrow.
 *
 * Gallery UX improvement: `vw` sizing is replaced with fixed px tile sizes (see gallery.css
 * `--sbp-gallery-tile`), but the reference's masonry COMPOSITION is preserved — images are
 * chunked into groups of three (`groupMedia`) and each group renders as one large square + two
 * stacked small squares, exactly the reference's large/small rhythm. Horizontal scrolling now
 * scrolls between these masonry GROUPS (`.sbp-gallery__scroller`), not between individual
 * equal-sized cards. A trailing group of 1 or 2 leftover images degrades gracefully (a single
 * large tile, or a two-tile stack) rather than distorting to fit the pattern.
 *
 * Public API unchanged (SectionComponentProps<'gallery'>) — Registry/Renderer/Engine untouched.
 * The reference gallery row sits in a tonal band (landing.style.css `.landing-row.gallery {
 * background:#f8f8f8 }`), rendered here with Section tone="alt". Images are bare <img> resolved
 * via the Asset Resolver — NOT the foundation Image wrapper, whose extra frame would break the
 * fixed-size grid math in gallery.css.
 */
import type { WheelEvent } from 'react'
import type { SectionComponentProps } from '../../runtime/types'
import type { AssetResolver } from '../../runtime/assets/assetResolver'
import type { MediaItem } from '../../schema'
import { Section, InfoBlock, Actions, cx } from '../shared'
import './gallery.css'

/** Lets a plain vertical mouse wheel scroll the strip horizontally (trackpads already do this natively). */
function handleWheel(event: WheelEvent<HTMLUListElement>) {
  const el = event.currentTarget
  if (el.scrollWidth <= el.clientWidth) return
  if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
  el.scrollLeft += event.deltaY
  event.preventDefault()
}

/** Chunks the media list into groups of 3 — the unit the large+stacked-small masonry pattern repeats over. */
function groupMedia(media: readonly MediaItem[]): MediaItem[][] {
  const groups: MediaItem[][] = []
  for (let i = 0; i < media.length; i += 3) groups.push(media.slice(i, i + 3))
  return groups
}

function GalleryImg({
  item,
  index,
  assets,
  className,
}: {
  item: MediaItem
  index: number
  assets: AssetResolver
  className: string
}) {
  const asset = assets.resolve(item.assetId)
  return <img key={`${item.assetId}-${index}`} className={className} src={asset.url} alt={asset.alt ?? ''} />
}

export function Gallery(p: SectionComponentProps<'gallery'>) {
  const { content, layout, id, theme, locale, assets } = p

  const media = content.media ?? []
  const groups = groupMedia(media)
  const titleId = `${id}-title`

  return (
    <Section
      as="section"
      tone="alt"
      theme={theme}
      locale={locale}
      id={id}
      ariaLabelledBy={content.heading ? titleId : undefined}
      className={cx('sbp-gallery', `sbp-gallery--${layout}`)}
    >
      <div className="sbp-gallery__info">
        <InfoBlock
          heading={content.heading}
          subheading={content.subheading}
          headingLevel={2}
          headingId={titleId}
          align="center"
        />
      </div>

      {groups.length > 0 ? (
        <ul className="sbp-gallery__scroller" onWheel={handleWheel}>
          {groups.map((group, gi) => (
            <li key={gi} className="sbp-gallery__group">
              {group.length === 3 ? (
                <div className="sbp-gallery__cell sbp-gallery__cell--triple">
                  <GalleryImg item={group[0]} index={0} assets={assets} className="sbp-gallery__img sbp-gallery__img--large" />
                  <div className="sbp-gallery__stack">
                    <GalleryImg item={group[1]} index={1} assets={assets} className="sbp-gallery__img sbp-gallery__img--small" />
                    <GalleryImg item={group[2]} index={2} assets={assets} className="sbp-gallery__img sbp-gallery__img--small" />
                  </div>
                </div>
              ) : group.length === 2 ? (
                <div className="sbp-gallery__stack sbp-gallery__stack--standalone">
                  <GalleryImg item={group[0]} index={0} assets={assets} className="sbp-gallery__img sbp-gallery__img--small" />
                  <GalleryImg item={group[1]} index={1} assets={assets} className="sbp-gallery__img sbp-gallery__img--small" />
                </div>
              ) : (
                <GalleryImg item={group[0]} index={0} assets={assets} className="sbp-gallery__img sbp-gallery__img--large" />
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {content.actions && content.actions.length > 0 ? (
        <div className="sbp-gallery__actions">
          <Actions actions={content.actions} align="center" size="lg" />
        </div>
      ) : null}
    </Section>
  )
}
